import type { FC } from 'react'
import { useCallback, useMemo } from 'react'

import useTranslation from '@space-lib/hooks/useTranslation'
import { useUserPermissions } from '@space-shell/common/hooks/useUserPermissions'
import {
  CommunityMemberPermissionsGroup,
  PermissionActions
} from '@space-shell/common/services/communities'
import { useGetCommunity } from '@space-shell/modules/communities/hooks'

import type { ModerationListActionOption, ModerationListActionProps } from '.'
import ModerationListAction from './moderationListAction'
import { DisplayBtn } from './moderationListActionStyles'

import CustomSpinner from '../moderationTableActions/roleAction/customSpinner'

const ModerationListActionWrapper: FC<ModerationListActionProps> = ({ communityId, member }) => {
  const { t } = useTranslation()
  const { communityData, isLoadingCommunityData } = useGetCommunity(Number(communityId))
  const { hasPermission } = useUserPermissions()
  const isTheOwner = useCallback(
    (id: number) => {
      return communityData?.ownerId === id
    },
    [communityData]
  )

  const options = useMemo<ModerationListActionOption[]>(() => {
    const opts = [
      {
        label: t('configuration_sidebar.members_and_permissions.roles.admin'),
        value: CommunityMemberPermissionsGroup.ADMIN,
        requiredPermission: PermissionActions.ADMIN_UPDATE,
        isVisible: false
      },
      {
        label: t('configuration_sidebar.members_and_permissions.roles.moderator'),
        value: CommunityMemberPermissionsGroup.MODERATOR,
        requiredPermission: PermissionActions.MODERATOR_UPDATE,
        isVisible: true
      },
      {
        label: t('configuration_sidebar.members_and_permissions.roles.member'),
        value: CommunityMemberPermissionsGroup.FOLLOWER,
        requiredPermission: PermissionActions.MEMBER_UPDATE,
        isVisible: true
      }
    ]

    return opts
  }, [t])

  const canEdit = useMemo(() => {
    return options.some((opt) =>
      hasPermission(opt.requiredPermission, communityData?.permissions?.actions)
    )
  }, [communityData?.permissions?.actions, hasPermission, options])

  const currentMemberOpt = useMemo(
    () => options.find((opt) => opt.value === member.memberType)?.label,
    [options, member.memberType]
  )

  const visibleOptions = options.filter((opt) => opt.isVisible)

  if (isLoadingCommunityData) {
    return <CustomSpinner />
  }

  if (!canEdit || isTheOwner(member.id)) {
    return currentMemberOpt ? (
      <DisplayBtn
        className='_d-flex _px-4 _py-2 _ml-auto _align-items-center _justify-content-center _border-0 _text-gray-600 _bg-transparent'
        disabled>
        {currentMemberOpt}
      </DisplayBtn>
    ) : null
  }

  return (
    <ModerationListAction
      communityId={Number(communityId)}
      member={member}
      options={visibleOptions}
    />
  )
}

export default ModerationListActionWrapper
